<template>
  <div class="card h-100">
    <div class="card-body">
      <div class="eyebrow-label mb-1">
        {{ $t("bulk_buy.purchase_settings") }}
      </div>
      <p class="text-secondary small mb-3">
        {{ $t("bulk_buy.sourcing_preference_description") }}
      </p>

      <div class="d-flex flex-column gap-2">
        <button
          v-for="card in sourcingCards"
          :key="card.value"
          type="button"
          class="sourcing-card"
          :class="{
            'sourcing-card--selected': model.purchase_mode === card.value,
          }"
          @click="selectMode(card.value)"
        >
          <span class="sourcing-card__radio" aria-hidden="true">
            <span
              v-if="model.purchase_mode === card.value"
              class="sourcing-card__dot"
            />
          </span>
          <span class="d-flex flex-column text-start">
            <span class="sourcing-card__title">{{ $t(card.titleKey) }}</span>
            <span class="sourcing-card__desc">{{ $t(card.descKey) }}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GlobalPurchaseSettings } from "./bulkBuyTypes";
import { PurchaseMode } from "./bulkBuyTypes";

const model = defineModel<GlobalPurchaseSettings>({ required: true });

const emit = defineEmits<{ "user-select": [mode: PurchaseMode] }>();

const sourcingCards = [
  {
    value: PurchaseMode.CHEAPEST,
    titleKey: "bulk_buy.sourcing_cheapest_title",
    descKey: "bulk_buy.sourcing_cheapest_desc",
  },
  {
    value: PurchaseMode.GHG,
    titleKey: "bulk_buy.sourcing_ghg_title",
    descKey: "bulk_buy.sourcing_ghg_desc",
  },
  {
    value: PurchaseMode.RE100,
    titleKey: "bulk_buy.sourcing_re100_title",
    descKey: "bulk_buy.sourcing_re100_desc",
  },
  {
    value: PurchaseMode.OTHER,
    titleKey: "bulk_buy.sourcing_other_title",
    descKey: "bulk_buy.sourcing_other_desc",
  },
] as const;

// Selecting a non-Other card clears any stale custom requirement so it never
// leaks into a priced order.
const selectMode = (mode: PurchaseMode) => {
  model.value = {
    ...model.value,
    purchase_mode: mode,
    custom_requirement:
      mode === PurchaseMode.OTHER ? model.value.custom_requirement : undefined,
  };
  emit("user-select", mode);
};
</script>

<style scoped lang="scss">
.sourcing-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.6rem 0.8rem;
  background: var(--phoenix-emphasis-bg);
  border: 1px solid
    var(--phoenix-border-color-strong, var(--phoenix-border-color));
  border-radius: 9px;
  cursor: pointer;
  transition:
    border-color 0.12s ease-out,
    background-color 0.12s ease-out;

  &:hover {
    border-color: var(--phoenix-primary);
  }

  // Primary border + inset ring instead of a wider border avoids any layout
  // shift between the selected/unselected states.
  &--selected {
    background: var(--phoenix-primary-bg-subtle);
    border-color: var(--phoenix-primary);
    box-shadow: inset 0 0 0 1px var(--phoenix-primary);
  }
}

.sourcing-card__radio {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid
    var(--phoenix-border-color-strong, var(--phoenix-border-color));

  .sourcing-card--selected & {
    border-color: var(--phoenix-primary);
  }
}

.sourcing-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--phoenix-primary);
}

.sourcing-card__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--phoenix-body-color);
}

.sourcing-card__desc {
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--phoenix-secondary-color);
}
</style>
